import { createContext, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import {
  KNOWN_CHAT_MODEL_VALUES,
  myWorkspacesResponseSchema,
  type KnownChatModelValue,
  type MyWorkspace,
} from '@mindkeep/shared'
import { ApiError, apiRequest, setRequestWorkspaceId } from '@/lib/api-client'
import { queryClient } from '@/lib/query-client'
import { readInviteToken } from '@/lib/invite'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth/auth-provider'
import { ErrorState } from '@/components/error-state'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

const ACTIVE_WORKSPACE_STORAGE_KEY = 'mindkeep.active-workspace-id'

const workspaceRoleSchema = z.enum(['owner', 'admin', 'member', 'viewer'])
export type WorkspaceRole = z.infer<typeof workspaceRoleSchema>

const WorkspaceSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  default_model: z.string(),
  allowed_chat_models: z
    .array(z.string().refine((m) => KNOWN_CHAT_MODEL_VALUES.has(m as KnownChatModelValue)))
    .default([...KNOWN_CHAT_MODEL_VALUES]),
  sector_pack: z
    .object({ id: z.string(), label: z.string(), version: z.string() })
    .nullable()
    .optional(),
  role: workspaceRoleSchema.nullable().optional(),
})

export type Workspace = z.infer<typeof WorkspaceSchema>

const PHASE_A_WORKSPACE: Workspace = {
  id: 'phase-a',
  name: 'MMIND Team',
  slug: 'mmind-team',
  default_model: 'mistral/mistral-large-latest',
  allowed_chat_models: [...KNOWN_CHAT_MODEL_VALUES],
  sector_pack: null,
  role: null,
}

interface WorkspaceContextValue {
  workspace: Workspace
  workspaces: MyWorkspace[]
  role: WorkspaceRole | null
  isAdmin: boolean
  isLoading: boolean
  roleLoading: boolean
  roleError: boolean
  switchWorkspace: (id: string) => void
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null)

function extractInviteToken(input: string): string {
  const trimmed = input.trim()
  const fromUrl = /einladung\/([^/?\s]+)/.exec(trimmed)
  return fromUrl?.[1] ?? trimmed
}

function readStoredWorkspaceId(): string | null {
  try {
    const value = window.localStorage.getItem(ACTIVE_WORKSPACE_STORAGE_KEY)
    return value && value.length > 0 ? value : null
  } catch {
    return null
  }
}

function writeStoredWorkspaceId(id: string): void {
  try {
    window.localStorage.setItem(ACTIVE_WORKSPACE_STORAGE_KEY, id)
  } catch {
    /* private mode / quota */
  }
}

function clearStoredWorkspaceId(): void {
  try {
    window.localStorage.removeItem(ACTIVE_WORKSPACE_STORAGE_KEY)
  } catch {
    /* ignore */
  }
}

function resolveActiveWorkspaceId(workspaces: MyWorkspace[]): string | null {
  if (workspaces.length === 0) return null
  const stored = readStoredWorkspaceId()
  if (stored && workspaces.some((item) => item.id === stored)) return stored
  if (stored) clearStoredWorkspaceId()
  return workspaces[0]!.id
}

function NoWorkspaceCard({
  storedInviteToken,
  inviteInput,
  setInviteInput,
  onLogout,
}: {
  storedInviteToken: string | null
  inviteInput: string
  setInviteInput: (value: string) => void
  onLogout: () => void
}) {
  const navigate = useNavigate()
  return (
    <main className="flex min-h-screen items-center justify-center px-4 py-10">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Noch kein Workspace</CardTitle>
          <CardDescription>
            Dein Konto ist noch keinem Workspace zugeordnet. Löse eine Einladung ein oder
            kontaktiere hartmut@huebner.io.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {storedInviteToken ? (
            <Button
              type="button"
              className="w-full"
              onClick={() => navigate(`/einladung/${encodeURIComponent(storedInviteToken)}`)}
            >
              Einladung annehmen
            </Button>
          ) : null}
          <div className="space-y-2">
            <Label htmlFor="no-workspace-invite-token">Einladungs-Code</Label>
            <div className="flex gap-2">
              <Input
                id="no-workspace-invite-token"
                value={inviteInput}
                onChange={(event) => setInviteInput(event.target.value)}
                placeholder="Code oder Link aus der Einladung"
              />
              <Button
                type="button"
                variant="secondary"
                data-testid="no-workspace-invite"
                disabled={!inviteInput.trim()}
                onClick={() => navigate(`/einladung/${encodeURIComponent(extractInviteToken(inviteInput))}`)}
              >
                Einladung öffnen
              </Button>
            </div>
          </div>
          <Button type="button" variant="outline" className="w-full" onClick={onLogout}>
            Abmelden
          </Button>
        </CardContent>
      </Card>
    </main>
  )
}

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [inviteInput, setInviteInput] = useState('')
  const storedInviteToken = readInviteToken()

  const mineQuery = useQuery({
    queryKey: ['workspace', 'mine'],
    queryFn: () =>
      apiRequest({
        path: '/workspaces/mine',
        schema: myWorkspacesResponseSchema,
      }),
    enabled: isAuthenticated,
    retry: false,
  })

  const workspaces = mineQuery.data?.workspaces ?? []
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const activeId =
    selectedId && workspaces.some((item) => item.id === selectedId)
      ? selectedId
      : mineQuery.data
        ? resolveActiveWorkspaceId(workspaces)
        : null

  useEffect(() => {
    if (!mineQuery.data) return
    const resolved = resolveActiveWorkspaceId(mineQuery.data.workspaces)
    setSelectedId((current) => {
      if (current && mineQuery.data!.workspaces.some((item) => item.id === current)) {
        return current
      }
      return resolved
    })
  }, [mineQuery.data])

  useEffect(() => {
    if (activeId) {
      setRequestWorkspaceId(activeId)
      writeStoredWorkspaceId(activeId)
      return
    }
    setRequestWorkspaceId(null)
  }, [activeId])

  const {
    data: workspace,
    isLoading,
    isPlaceholderData,
    isError,
    error,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ['workspace', 'current', activeId],
    queryFn: () =>
      apiRequest({
        path: '/workspaces/me',
        schema: WorkspaceSchema,
      }),
    enabled: isAuthenticated && !!activeId,
    retry: false,
    placeholderData: PHASE_A_WORKSPACE,
  })

  const mineEntry = workspaces.find((item) => item.id === activeId)
  const effectiveWorkspace =
    workspace && workspace.id === activeId
      ? workspace
      : mineEntry
        ? {
            ...PHASE_A_WORKSPACE,
            id: mineEntry.id,
            name: mineEntry.name,
            slug: mineEntry.slug,
            role: mineEntry.role,
          }
        : PHASE_A_WORKSPACE
  const role = effectiveWorkspace.role ?? null

  function switchWorkspace(id: string) {
    if (!workspaces.some((item) => item.id === id) || id === activeId) return
    writeStoredWorkspaceId(id)
    setSelectedId(id)
    setRequestWorkspaceId(id)
    queryClient.removeQueries({
      predicate: (query) => query.queryKey[0] !== 'workspace',
    })
    void queryClient.invalidateQueries()
  }

  async function handleLogout() {
    await supabase.auth.signOut()
    setRequestWorkspaceId(null)
    navigate('/login', { replace: true })
  }

  const contextValue: WorkspaceContextValue = {
    workspace: effectiveWorkspace,
    workspaces,
    role,
    isAdmin: role === 'owner' || role === 'admin',
    isLoading: (mineQuery.isLoading && !mineQuery.data) || (isLoading && !workspace),
    roleLoading: mineQuery.isLoading || isPlaceholderData || (isLoading && !workspace),
    roleError: mineQuery.isError || isError,
    switchWorkspace,
  }

  if (isAuthenticated && mineQuery.isLoading && !mineQuery.data) {
    return (
      <main className="flex min-h-screen items-center justify-center px-4">
        <p className="text-sm text-muted-foreground">Workspace wird geladen</p>
      </main>
    )
  }

  if (mineQuery.isSuccess && workspaces.length === 0) {
    return (
      <NoWorkspaceCard
        storedInviteToken={storedInviteToken}
        inviteInput={inviteInput}
        setInviteInput={setInviteInput}
        onLogout={() => void handleLogout()}
      />
    )
  }

  if (mineQuery.isError || isError) {
    const noWorkspace =
      error instanceof ApiError && (error.status === 403 || error.code === 'no_workspace')
    if (noWorkspace) {
      return (
        <NoWorkspaceCard
          storedInviteToken={storedInviteToken}
          inviteInput={inviteInput}
          setInviteInput={setInviteInput}
          onLogout={() => void handleLogout()}
        />
      )
    }

    return (
      <main className="flex min-h-screen items-center justify-center px-4 py-10">
        <ErrorState
          className="w-full max-w-md"
          title="Workspace konnte nicht geladen werden"
          description={
            mineQuery.error instanceof Error
              ? mineQuery.error.message
              : error instanceof Error
                ? error.message
                : undefined
          }
          onRetry={() => {
            void mineQuery.refetch()
            void refetch()
          }}
          isRetrying={mineQuery.isRefetching || isRefetching}
        />
      </main>
    )
  }

  return (
    <WorkspaceContext.Provider value={contextValue}>
      {children}
    </WorkspaceContext.Provider>
  )
}

export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext)
  if (!ctx) throw new Error('useWorkspace muss innerhalb von WorkspaceProvider verwendet werden')
  return ctx
}
